import type { ReactNode } from 'react';
import { V2Card, V2CardHeader } from '@sakhtar/design-system/react';
import type { AmountDisplayUnit } from '../../lib/finance-display.js';
import { agingBucketLabel, amountUnitLabel, formatRialFa } from '../../lib/finance-display.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type { AgingBucket } from '../../lib/types/finance.js';
import { AGING_BUCKET_COLOR, agingShares, formatPermille } from './OpenItemAggregates.js';

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — /t/kimia/finance, finAr «سنی مطالبات»
 *   (and the matching AP aging) — segmented severity bar + one tile per bucket
 *   (label, amount, share).
 * status: PARITY — buckets are the server's `GET .../finance/{ar|ap}/aging`
 *   `buckets` (apps/api/src/finance/subledger-serialize.ts `serializeAging`);
 *   shares are derived from them with BigInt, never from a paged subset. The
 *   reference's «۱٫۹۴ میلیارد» compact subtitle becomes the exact total.
 */
export function OpenItemsAgingCard({
  kind,
  buckets,
  asOfDate,
  unit,
  headerActions,
  children,
}: {
  readonly kind: 'ar' | 'ap';
  readonly buckets: readonly AgingBucket[];
  readonly asOfDate: string;
  readonly unit: AmountDisplayUnit;
  readonly headerActions?: ReactNode;
  readonly children?: ReactNode;
}) {
  const { shares, totalRial } = agingShares(buckets);
  const title = kind === 'ar' ? 'سنی مطالبات' : 'سنی بدهی‌ها';
  const itemCount = buckets.reduce((n, b) => n + b.itemCount, 0);
  return (
    <V2Card>
      <V2CardHeader
        icon="trend"
        title={title}
        subtitle={`مانده بر اساس روزهای گذشته از سررسید تا ${displayJalaliDate(asOfDate)} · ${formatRialFa(
          totalRial,
          unit,
        )} ${amountUnitLabel(unit)} · ${itemCount.toLocaleString('fa-IR')} قلم`}
        actions={headerActions}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 14 }}>
        <div
          role="img"
          aria-label={`${title}: ${shares
            .map((s) => `${agingBucketLabel(s.bucket)} ${formatPermille(s.permille)}`)
            .join('، ')}`}
          style={{ display: 'flex', gap: 3, height: 14 }}
        >
          {shares.map((s) => (
            <span
              key={s.bucket}
              style={{
                flex: `${s.permille > 0 ? s.permille : 0} 1 0px`,
                minWidth: s.permille > 0 ? 4 : 0,
                borderRadius: 4,
                background: AGING_BUCKET_COLOR[s.bucket] ?? 'var(--sk-fg-subtle)',
              }}
            />
          ))}
          {BigInt(totalRial) === 0n ? (
            <span
              style={{ flex: 1, borderRadius: 4, background: 'var(--sk-bg-sunken)' }}
              aria-hidden
            />
          ) : null}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: 8,
          }}
        >
          {shares.map((s) => (
            <div
              key={s.bucket}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                padding: '8px 10px',
                borderRadius: 10,
                background: 'var(--sk-bg-sunken)',
              }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 11.5,
                  color: 'var(--sk-fg-muted)',
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    flex: '0 0 8px',
                    borderRadius: 2,
                    background: AGING_BUCKET_COLOR[s.bucket] ?? 'var(--sk-fg-subtle)',
                  }}
                />
                {agingBucketLabel(s.bucket)}
              </span>
              <span className="sk-num" style={{ fontSize: 15, fontWeight: 700 }}>
                {formatRialFa(s.openBalanceRial, unit)}
              </span>
              <span className="sk-num" style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
                {formatPermille(s.permille)} · {s.itemCount.toLocaleString('fa-IR')} قلم
              </span>
            </div>
          ))}
        </div>
        {children}
      </div>
    </V2Card>
  );
}
